<script setup>
import { computed, inject } from 'vue';

import ChannelIcon from 'next/icon/ChannelIcon.vue';
import FontIcon from 'next/icon/FontIcon.vue';
import { getChannelSvgUrlForInbox } from 'dashboard/components-next/icon/channelSvgUrls';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
  size: {
    type: String,
    default: 'md',
    validator: v => ['xxs', 'xs', 'sm', 'md', 'lg'].includes(v),
  },
  /** When true, omits background and ring around the icon (e.g. dropdown list items). */
  bare: {
    type: Boolean,
    default: false,
  },
});

const customFontIcons = inject('customFontIcons', new Map());
const fontIcon = computed(() =>
  customFontIcons.get(props.inbox.channel_type || props.inbox.channelType)
);
const svgUrl = computed(() => getChannelSvgUrlForInbox(props.inbox) || '');
const fontSize = computed(
  () => ({ xxs: 14, xs: 18, sm: 28, md: 32, lg: 32 })[props.size]
);

const shellClass = computed(() => {
  const bareShell = {
    xxs: 'grid size-3.5 shrink-0 place-items-center',
    xs: 'grid size-[18px] shrink-0 place-items-center',
    sm: 'grid size-9 shrink-0 place-items-center',
    md: 'grid size-[36px] shrink-0 place-items-center',
    lg: 'grid size-14 shrink-0 place-items-center',
  };
  const framedShell = {
    sm: 'grid size-9 shrink-0 place-items-center rounded-full bg-n-solid-1/90 ring-1 ring-n-weak/80 dark:bg-n-alpha-3/90',
    lg: 'grid size-14 shrink-0 place-items-center rounded-full bg-n-solid-1/90 ring-1 ring-n-weak/80 dark:bg-n-alpha-3/90',
  };

  if (props.bare) return bareShell[props.size] || bareShell.md;
  return framedShell[props.size] || bareShell[props.size] || bareShell.md;
});

const imgClass = computed(
  () =>
    ({
      xxs: 'size-3.5 shrink-0 object-contain',
      xs: 'size-[18px] shrink-0 object-contain',
      sm: 'size-7 shrink-0 object-contain',
      md: 'size-[36px] shrink-0 object-contain',
      lg: 'size-10 shrink-0 object-contain',
    })[props.size]
);

const iconClass = computed(() => {
  const s =
    {
      xxs: 'size-3.5',
      xs: 'size-[18px]',
      sm: 'size-7',
      md: 'size-[36px]',
      lg: 'size-10',
    }[props.size] || 'size-8';
  return `${s} shrink-0 text-n-slate-10`;
});
</script>

<template>
  <div :class="shellClass">
    <FontIcon v-if="fontIcon" :icon="fontIcon" :size="fontSize" />
    <img
      v-else-if="svgUrl"
      :src="svgUrl"
      alt=""
      decoding="async"
      draggable="false"
      :class="imgClass"
    />
    <ChannelIcon v-else :inbox="inbox" :class="iconClass" />
  </div>
</template>
